import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ExternalLink, FileText } from 'lucide-react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DocumentClientBlock,
  DocumentItemsTable,
  DocumentMetaGrid,
  DocumentSection,
  DocumentTotals,
  type DocumentItem,
  type TotalLine,
} from '@/components/app-document-detail';

interface InvoiceDetail {
  invoice: {
    id: string;
    invoiceNumber: string;
    clientName: string;
    clientRuc: string | null;
    clientEmail: string | null;
    currency: string;
    paymentMethod: string | null;
    saleType: string;
    subtotal: string;
    tax5: string;
    tax10: string;
    taxExempt: string;
    grandTotal: string;
    status: string;
    cdc: string | null;
    timbrado: string | null;
    kudeUrl: string | null;
    isDemo: boolean;
    createdAt: string;
  };
  items: DocumentItem[];
}

const E_STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador',
  demo_approved: 'Emitida (demo)',
  issued: 'Emitida',
  sent: 'Enviada',
  void: 'Anulada',
  cancelled: 'Anulada',
  error: 'Error',
};

function eStatusVariant(s: string): 'success' | 'warning' | 'destructive' | 'secondary' {
  if (s === 'demo_approved' || s === 'issued' || s === 'sent') return 'success';
  if (s === 'void' || s === 'cancelled' || s === 'error') return 'destructive';
  return 'secondary';
}

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  giro: 'Giro',
  billetera: 'Billetera electrónica',
  tarjeta_empresarial: 'Tarjeta empresarial',
  vale: 'Vale',
  retencion: 'Retención',
};

export function ElectronicInvoiceDetailDialog({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['app-electronic-invoice', id],
    queryFn: () => api<InvoiceDetail>(`/api/app/electronic-invoices/${id}`),
    enabled: !!id,
  });

  const inv = data?.invoice;
  const currency = inv?.currency ?? 'PYG';

  const totalLines: TotalLine[] = inv
    ? [
        { label: 'Subtotal', value: inv.subtotal },
        ...(Number(inv.tax5) > 0 ? [{ label: 'IVA 5%', value: inv.tax5 }] : []),
        ...(Number(inv.tax10) > 0 ? [{ label: 'IVA 10%', value: inv.tax10 }] : []),
        ...(Number(inv.taxExempt) > 0 ? [{ label: 'Exentas', value: inv.taxExempt }] : []),
        { label: 'Total', value: inv.grandTotal, emphasis: true },
      ]
    : [];

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[95vw] max-w-5xl overflow-x-hidden overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <FileText className="size-4 text-muted-foreground" />
            Factura {inv?.invoiceNumber ?? ''}
            {inv?.isDemo && (
              <Badge variant="secondary" className="text-[10px]">
                demo
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading || !inv ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          // min-w-0: ver nota en presupuestos.tsx (grid child no encoge → desborda).
          <div className="min-w-0 space-y-5">
            <DocumentClientBlock
              name={inv.clientName}
              doc={inv.clientRuc}
              badge={
                <Badge variant={eStatusVariant(inv.status)}>
                  {E_STATUS_LABEL[inv.status] ?? inv.status}
                </Badge>
              }
            />

            <DocumentMetaGrid
              items={[
                { label: 'Condición', value: inv.saleType === 'credito' ? 'Crédito' : 'Contado' },
                {
                  label: 'Medio de pago',
                  value: inv.paymentMethod
                    ? (PAYMENT_METHOD_LABEL[inv.paymentMethod] ?? inv.paymentMethod)
                    : null,
                },
                { label: 'Moneda', value: currency },
                {
                  label: 'Emitida',
                  value: format(new Date(inv.createdAt), 'dd MMM yyyy', { locale: es }),
                },
                { label: 'Timbrado', value: inv.timbrado },
                {
                  label: 'CDC',
                  value: inv.cdc ? <span className="font-mono text-xs break-all">{inv.cdc}</span> : null,
                },
              ]}
            />

            <DocumentSection title="Ítems">
              <DocumentItemsTable items={data.items} currency={currency} showTax />
            </DocumentSection>

            <DocumentTotals lines={totalLines} currency={currency} />
          </div>
        )}

        <DialogFooter>
          {inv?.kudeUrl && (
            <Button variant="outline" asChild>
              <a href={inv.kudeUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink /> Ver KUDE
              </a>
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
